import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { GestureController } from '@ionic/angular';

@Directive({
  selector: '[appLongPress]',
})
export class LongPressDirective implements OnInit, OnDestroy {
  @Output() press = new EventEmitter();
  @Output() pressEnd = new EventEmitter();
  @Input() pressTime = 500; // Default press time in ms

  private gesture: any;
  private longPressActive = false;
  private longPressTriggered = false;
  private longPressTimeout: any;
  private touchStartX = 0;
  private touchStartY = 0;

  constructor(private el: ElementRef, private gestureCtrl: GestureController) {}

  ngOnInit() {
    this.initLongPressGesture();
    this.addNativeTouchListeners();
  }

  ngOnDestroy() {
    if (this.gesture) {
      this.gesture.destroy();
    }
    this.clearTimeout();
    this.removeNativeTouchListeners();
  }

  private clearTimeout() {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = null;
    }
  }

  private initLongPressGesture() {
    this.gesture = this.gestureCtrl.create({
      el: this.el.nativeElement,
      threshold: 0,
      gestureName: 'long-press',
      onStart: ev => {
        this.longPressActive = true;
        this.longPressTriggered = false;

        // Clear any existing timeout
        this.clearTimeout();

        // Set timeout for long press
        this.longPressTimeout = setTimeout(() => {
          if (this.longPressActive) {
            this.longPressTriggered = true;
            this.press.emit(ev);
          }
        }, this.pressTime);
      },
      onMove: ev => {
        // If finger moves too much, cancel the long press
        if (ev.deltaX > 5 || ev.deltaY > 5) {
          this.longPressActive = false;
          this.clearTimeout();
        }
      },
      onEnd: ev => {
        if (this.longPressTriggered) {
          this.pressEnd.emit(ev);
        }
        this.longPressActive = false;
        this.longPressTriggered = false;
        this.clearTimeout();
      },
      onCancel: () => {
        this.longPressActive = false;
        this.longPressTriggered = false;
        this.clearTimeout();
      },
    });

    this.gesture.enable(true);
  }

  // Add native touch event handlers as a fallback
  private addNativeTouchListeners() {
    const element = this.el.nativeElement;

    element.addEventListener('touchstart', this.onTouchStart, {
      passive: false,
    });
    element.addEventListener('touchend', this.onTouchEnd, { passive: false });
    element.addEventListener('touchcancel', this.onTouchCancel, {
      passive: false,
    });
    element.addEventListener('touchmove', this.onTouchMove, { passive: false });
  }

  private removeNativeTouchListeners() {
    const element = this.el.nativeElement;

    element.removeEventListener('touchstart', this.onTouchStart);
    element.removeEventListener('touchend', this.onTouchEnd);
    element.removeEventListener('touchcancel', this.onTouchCancel);
    element.removeEventListener('touchmove', this.onTouchMove);
  }

  private onTouchStart = (event: TouchEvent) => {
    this.longPressActive = true;
    this.longPressTriggered = false;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;

    // Clear any existing timeout
    this.clearTimeout();

    // Set timeout for long press
    this.longPressTimeout = setTimeout(() => {
      if (this.longPressActive) {
        this.longPressTriggered = true;
        this.press.emit(event);
        // Prevent default to avoid any conflicts
        event.preventDefault();
      }
    }, this.pressTime);
  };

  private onTouchMove = (event: TouchEvent) => {
    // Calculate movement
    const moveX = Math.abs(event.touches[0].clientX - this.touchStartX);
    const moveY = Math.abs(event.touches[0].clientY - this.touchStartY);

    // If moved more than threshold, cancel long press
    if (moveX > 5 || moveY > 5) {
      this.longPressActive = false;
      this.clearTimeout();
    }
  };

  private onTouchEnd = (event: TouchEvent) => {
    if (this.longPressTriggered) {
      this.pressEnd.emit(event);
    }
    this.longPressActive = false;
    this.longPressTriggered = false;
    this.clearTimeout();
  };

  private onTouchCancel = (event: TouchEvent) => {
    this.longPressActive = false;
    this.longPressTriggered = false;
    this.clearTimeout();
  };
}

